import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import api, {
  getAppUsersWithFilter,
  getProfessionalSituation,
  saveAppUserColumnConfig
} from '../helper/Api/AdminApi';
import AdvancedFilter from './AdvancedFilter';
import ColumnChooser from './ColumnChooser';
import FilterUserDrawer from './FilterUserDrawer';

const DEFAULT_COLUMNS = [
  { id: 'FullName', label: 'Full Name', visible: true, sortable: true },
  { id: 'EmailID', label: 'Email ID', visible: true, sortable: true },
  { id: 'ContactNumber', label: 'Contact Number', visible: true, sortable: true },
  { id: 'SubscriptionEndDate', label: 'Subscription End Date', visible: true, sortable: true },
  { id: 'DOB', label: 'Date of Birth', visible: true, sortable: true },
  { id: 'FirstName', label: 'First Name', visible: true, sortable: true },
  { id: 'LastName', label: 'Last Name', visible: true, sortable: true }
];

const HORIZON_OPTIONS = [
  'Inférieur à 1 an',
  'Entre 1 an et 5 ans',
  'Supérieur à 5 ans'
];

const OBJECTIVE_OPTIONS = [
  'Construire un capital',
  'Générer un revenu complémentaire',
  'Préparer ma retraite',
  'Spéculer à court terme'
];

const RISK_OPTIONS = [
  'Conservateur',
  'Modéré',
  'Entrepreneur'
];

const PAGE_SIZE_OPTIONS = [5, 10, 25, 50, 100, 'All'];

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return Number.isNaN(date.getTime()) ? dateString : date.toLocaleDateString();
};

const mapConfigToColumns = (config = []) => {
  if (!config || !config.length) {
    return DEFAULT_COLUMNS.map((column, index) => ({
      ...column,
      position: index + 1
    }));
  }

  const configMap = new Map(config.map((item) => [item.ColumnName, item]));

  const merged = DEFAULT_COLUMNS.map((column, index) => {
    const matched = configMap.get(column.id);
    if (matched) {
      const displayName = matched.DisplayName || column.label;
      const isVisible = matched.IsVisible === 1 || matched.IsVisible === true;
      const position = matched.Priority || matched.position || index + 1;

      return {
        ...column,
        label: displayName,
        visible: isVisible,
        position: position
      };
    }
    return {
      ...column,
      position: index + 1
    };
  });

  return merged.sort((a, b) => (a.position || 999) - (b.position || 999));
};

const buildColumnPayload = (columns) => columns.map((column, index) => ({
  ColumnName: column.id,
  Priority: index + 1,
  IsVisible: column.visible !== false
}));

const APP_ACTION_COLUMN_WIDTH = 76;

const normalizeAdvancedFilters = (filters) =>
  filters.map((filter) => ({
    ColumnName: filter.field,
    Operator: filter.operator,
    Value: filter.value
  }));

const hasAdvancedFilterValue = (value) => {
  if (Array.isArray(value)) {
    return value.every((item) => String(item || '').trim() !== '');
  }
  return String(value || '').trim() !== '';
};

/**
 * Build download blob from base64 data - same as UserQueriesReport
 */
const buildDownloadBase64 = (base64Data, contentType) => {
  let cleanBase64 = (base64Data || '').trim();
  if (cleanBase64.includes('base64,')) {
    cleanBase64 = cleanBase64.split('base64,')[1];
  }
  cleanBase64 = cleanBase64.replace(/\s/g, '');

  while (cleanBase64.length % 4 !== 0) {
    cleanBase64 += '=';
  }

  const binaryString = window.atob(cleanBase64);
  const bytes = new Uint8Array(binaryString.length);

  for (let index = 0; index < binaryString.length; index += 1) {
    bytes[index] = binaryString.charCodeAt(index);
  }

  return new Blob([bytes], { type: contentType });
};

const AppUserListReport = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [excelDownloadLoading, setExcelDownloadLoading] = useState(false);
  const [professionalSituations, setProfessionalSituations] = useState([]);
  const [allColumns, setAllColumns] = useState(DEFAULT_COLUMNS);
  const [draggedCol, setDraggedCol] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchColumn, setSearchColumn] = useState('EmailID');
  const [searchType, setSearchType] = useState('contains');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [sortColumn, setSortColumn] = useState('ProfileCreationDate');
  const [sortOrder, setSortOrder] = useState('desc');
  const [advanceFilters, setAdvanceFilters] = useState([]);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [filterForm, setFilterForm] = useState({
    profileDateEnabled: false,
    fromProfileCreationDate: '',
    toProfileCreationDate: '',
    subscriptionDateEnabled: false,
    fromSubscriptionEndDate: '',
    toSubscriptionEndDate: '',
    situationProfessionnelle: '',
    horizonDePlacement: '',
    objectifDePlacement: [],
    aversionAuRisque: ''
  });
  const [objectiveDropdownOpen, setObjectiveDropdownOpen] = useState(false);
  const objectiveDropdownRef = useRef(null);

  const isFirstRender = useRef(true);
  const isFetching = useRef(false);
  const pendingFilterApply = useRef(false);
  const isDraggingRef = useRef(false);

  const appliedFiltersRef = useRef({
    isFilterApplied: false,
    page: 1,
    limit: 10,
    sortColumn: 'ProfileCreationDate',
    sortOrder: 'desc',
    advanceFilters: [],
    profileDateEnabled: false,
    fromProfileCreationDate: '',
    toProfileCreationDate: '',
    subscriptionDateEnabled: false,
    fromSubscriptionEndDate: '',
    toSubscriptionEndDate: '',
    situationProfessionnelle: '',
    horizonDePlacement: '',
    objectifDePlacement: [],
    aversionAuRisque: ''
  });

  const visibleColumns = useMemo(
    () => allColumns.filter((column) => column.visible),
    [allColumns]
  );

  const filterFields = useMemo(
    () => [
      { value: 'FullName', label: 'Full Name' },
      { value: 'EmailID', label: 'Email ID' },
      { value: 'ContactNumber', label: 'Contact Number' },
      { value: 'DOB', label: 'Date of Birth' },
      { value: 'FirstName', label: 'First Name' },
      { value: 'LastName', label: 'Last Name' }
    ],
    []
  );

  const searchTypeOptions = ['contains', 'starts with', 'ends with'];

  const hasChildFilterValues = useMemo(() => (
    (filterForm.profileDateEnabled && (
      filterForm.fromProfileCreationDate ||
      filterForm.toProfileCreationDate
    )) ||
    (filterForm.subscriptionDateEnabled && (
      filterForm.fromSubscriptionEndDate ||
      filterForm.toSubscriptionEndDate
    )) ||
    !!filterForm.situationProfessionnelle ||
    !!filterForm.horizonDePlacement ||
    !!filterForm.objectifDePlacement.length ||
    !!filterForm.aversionAuRisque
  ), [filterForm]);

  const syncColumnsFromResponse = useCallback((response) => {
    let configSource = null;

    if (response?.UserConfig && Array.isArray(response.UserConfig) && response.UserConfig.length > 0) {
      configSource = response.UserConfig;
    } else if (response?.Config && Array.isArray(response.Config) && response.Config.length > 0) {
      configSource = response.Config;
    }

    if (configSource && configSource.length > 0) {
      const mappedColumns = mapConfigToColumns(configSource);
      setAllColumns(mappedColumns);
    }
  }, []);

  const buildFilterPayload = useCallback((outputType = 'json', page = 1, limit = pageSize) => {
    const filters = appliedFiltersRef.current;

    const hasFilters = !!(filters.profileDateEnabled ||
      filters.subscriptionDateEnabled ||
      filters.situationProfessionnelle ||
      filters.horizonDePlacement ||
      filters.objectifDePlacement?.length ||
      filters.aversionAuRisque ||
      filters.advanceFilters?.length);

    const payload = {
      IsViewAllChecked: !hasFilters,
      Page: page,
      Limit: limit === 'All' ? 1000 : limit,
      OutPutType: outputType,
      SortingColumn: filters.sortColumn || 'ProfileCreationDate',
      SortOrder: filters.sortOrder || 'desc'
    };

    if (filters.profileDateEnabled) {
      payload.IsFilterAppOnProfileCreationDate = true;
      payload.FromProfileCreationDate = filters.fromProfileCreationDate;
      payload.ToProfileCreationDate = filters.toProfileCreationDate;
    }

    if (filters.subscriptionDateEnabled) {
      payload.IsFilterAppOnSubEndDate = true;
      payload.FromSubscriptionEndDate = filters.fromSubscriptionEndDate;
      payload.ToSubscriptionEndDate = filters.toSubscriptionEndDate;
    }

    if (filters.situationProfessionnelle) {
      payload.SituationProfessionnelle = filters.situationProfessionnelle;
    }

    if (filters.horizonDePlacement) {
      payload.HorizonDePlacement = filters.horizonDePlacement;
    }

    if (filters.objectifDePlacement?.length) {
      payload.ObjectifDePlacement = filters.objectifDePlacement;
    }

    if (filters.aversionAuRisque) {
      payload.AversionAuRisque = filters.aversionAuRisque;
    }

    if (filters.advanceFilters?.length) {
      payload.AdvanceFilter = normalizeAdvancedFilters(filters.advanceFilters);
    }

    return payload;
  }, [pageSize]);

  const fetchUsers = useCallback(async (page = 1, limit = pageSize, useAppliedFilters = false, sortColOverride = null, sortOrdOverride = null) => {
    if (isFetching.current) return [];
    isFetching.current = true;

    setLoading(true);
    try {
      const activeSortCol = sortColOverride || appliedFiltersRef.current.sortColumn || sortColumn || 'ProfileCreationDate';
      const activeSortOrd = sortOrdOverride || appliedFiltersRef.current.sortOrder || sortOrder || 'desc';

      const payload = useAppliedFilters
        ? buildFilterPayload('json', page, limit)
        : {
          IsViewAllChecked: true,
          Page: page,
          Limit: limit === 'All' ? 1000 : limit,
          OutPutType: 'json',
          SortingColumn: activeSortCol,
          SortOrder: activeSortOrd
        };

      const response = await getAppUsersWithFilter(payload);
      const rows = (response?.Data || []).filter((user) => user.status !== 'Active');
      setUsers(rows);
      setTotalRecords(response?.TotalCount || response?.pagination?.total_records || rows.length || 0);
      setTotalPages(response?.pagination?.total_pages || 0);
      syncColumnsFromResponse(response);
      return rows;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to fetch app users');
      setUsers([]);
      setTotalRecords(0);
      setTotalPages(0);
      return [];
    } finally {
      setLoading(false);
      isFetching.current = false;
      pendingFilterApply.current = false;
    }
  }, [buildFilterPayload, syncColumnsFromResponse, sortColumn, sortOrder, pageSize]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      const initialPayload = {
        IsViewAllChecked: true,
        Page: 1,
        Limit: 10,
        OutPutType: 'json',
        SortingColumn: 'ProfileCreationDate',
        SortOrder: 'desc'
      };

      const fetchInitialUsers = async () => {
        try {
          setLoading(true);
          const response = await getAppUsersWithFilter(initialPayload);
          const rows = (response?.Data || []).filter((user) => user.status !== 'Active');
          setUsers(rows);
          setTotalRecords(response?.TotalCount || response?.pagination?.total_records || rows.length || 0);
          setTotalPages(response?.pagination?.total_pages || 0);
          syncColumnsFromResponse(response);
        } catch (error) {
          toast.error(error.response?.data?.message || 'Failed to fetch app users');
          setUsers([]);
          setTotalRecords(0);
          setTotalPages(0);
        } finally {
          setLoading(false);
          isFetching.current = false;
        }
      };

      fetchInitialUsers();
    }
  }, [syncColumnsFromResponse]);

  useEffect(() => {
    const fetchProfessionalSituations = async () => {
      try {
        const response = await getProfessionalSituation();
        const values = response?.data?.value || response?.Data || response?.value || [];
        const activeOnly = values.filter((item) =>
          item.IsActive === '1' || item.IsActive === 1 || item.IsActive === true || item.IsActive === undefined
        );
        setProfessionalSituations(activeOnly);
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to load professional situations');
      }
    };

    fetchProfessionalSituations();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (objectiveDropdownRef.current && !objectiveDropdownRef.current.contains(event.target)) {
        setObjectiveDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const paginatedUsers = useMemo(() => {
    if (isFilterApplied) return users;

    if (!searchTerm.trim()) {
      return users;
    }

    const filtered = users.filter((user) => {
      const searchLower = searchTerm.toLowerCase();
      const value = searchColumn === 'EmailID' ? user.email || '' : user.fullName || '';
      const normalizedValue = value.toLowerCase();
      if (searchType === 'starts with') return normalizedValue.startsWith(searchLower);
      if (searchType === 'ends with') return normalizedValue.endsWith(searchLower);
      return normalizedValue.includes(searchLower);
    });

    const startIndex = pageSize === 'All' ? 0 : (currentPage - 1) * pageSize;
    const endIndex = pageSize === 'All' ? filtered.length : Math.min(currentPage * pageSize, filtered.length);
    return pageSize === 'All' ? filtered : filtered.slice(startIndex, endIndex);
  }, [currentPage, isFilterApplied, pageSize, searchColumn, searchTerm, searchType, users]);

  const localFilteredCount = useMemo(() => {
    if (isFilterApplied) return totalRecords;
    if (!searchTerm.trim()) return totalRecords;
    return users.filter((user) => {
      const searchLower = searchTerm.toLowerCase();
      const value = searchColumn === 'EmailID' ? user.email || '' : user.fullName || '';
      const normalizedValue = value.toLowerCase();
      if (searchType === 'starts with') return normalizedValue.startsWith(searchLower);
      if (searchType === 'ends with') return normalizedValue.endsWith(searchLower);
      return normalizedValue.includes(searchLower);
    }).length;
  }, [isFilterApplied, searchColumn, searchTerm, searchType, totalRecords, users]);

  const effectiveTotalPages = useMemo(() => {
    if (isFilterApplied) {
      return totalPages;
    }
    if (searchTerm.trim()) {
      return Math.ceil(localFilteredCount / (pageSize === 'All' ? localFilteredCount || 1 : pageSize));
    }
    return totalPages;
  }, [isFilterApplied, totalPages, searchTerm, localFilteredCount, pageSize]);

  const startEntry = useMemo(() => {
    if (isFilterApplied) {
      return users.length ? (currentPage - 1) * (pageSize === 'All' ? users.length : pageSize) + 1 : 0;
    }
    if (searchTerm.trim()) {
      return pageSize === 'All' ? (paginatedUsers.length ? 1 : 0) : Math.min((currentPage - 1) * pageSize + 1, localFilteredCount || 0);
    }
    return pageSize === 'All' ? (users.length ? 1 : 0) : (currentPage - 1) * pageSize + 1;
  }, [isFilterApplied, currentPage, pageSize, users.length, searchTerm, paginatedUsers.length, localFilteredCount]);

  const endEntry = useMemo(() => {
    if (isFilterApplied) {
      return Math.min((currentPage - 1) * (pageSize === 'All' ? users.length : pageSize) + users.length, totalRecords);
    }
    if (searchTerm.trim()) {
      return pageSize === 'All' ? paginatedUsers.length : Math.min(currentPage * pageSize, localFilteredCount);
    }
    return Math.min(currentPage * pageSize, totalRecords);
  }, [isFilterApplied, currentPage, pageSize, users.length, totalRecords, searchTerm, paginatedUsers.length, localFilteredCount]);

  const handleViewDetails = (user) => {
    setSelectedUser({
      id: user.id,
      userID: user.id || user.userID,
      fullName: user.fullName || '',
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      contactNumber: user.contactNumber || '',
      email: user.email || '',
      dob: user.dob || '',
      emailAlert: user.emailAlert || false,
      pushAlert: user.pushAlert || false,
      subscriptionEndingDate: user.subscriptionEndingDate || null,
      InvoiceHistory: user.InvoiceHistory || [],
      name: user.fullName || user.firstName || ''
    });
    setShowDrawer(true);
  };

  const handleCloseDrawer = () => {
    setShowDrawer(false);
    setSelectedUser(null);
  };

  const handleUpdateSuccess = async () => {
    const rows = await fetchUsers(currentPage, pageSize, isFilterApplied);
    if (selectedUser && rows.length) {
      const refreshed = rows.find((user) => user.id === selectedUser.id || user.userID === selectedUser.userID);
      if (refreshed) {
        setSelectedUser((previous) => ({
          ...previous,
          ...refreshed
        }));
      }
    }
  };

  const handleUpdateUser = (updatedUser) => {
    setSelectedUser((previous) => ({
      ...previous,
      ...updatedUser
    }));
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);

    appliedFiltersRef.current.page = 1;
    appliedFiltersRef.current.limit = size;
    fetchUsers(1, size, isFilterApplied);
  };

  const handlePageChange = (page) => {
    if (page === currentPage) return;
    setCurrentPage(page);
    appliedFiltersRef.current.page = page;
    fetchUsers(page, pageSize, isFilterApplied);
  };

  const handleSort = (column) => {
    if (!column.sortable) return;

    const nextOrder = sortColumn === column.id ? (sortOrder === 'desc' ? 'asc' : 'desc') : 'desc';

    setSortColumn(column.id);
    setSortOrder(nextOrder);

    appliedFiltersRef.current.sortColumn = column.id;
    appliedFiltersRef.current.sortOrder = nextOrder;
    appliedFiltersRef.current.page = 1;
    setCurrentPage(1);

    fetchUsers(1, pageSize, isFilterApplied, column.id, nextOrder);
  };

  const handleResetColumns = async () => {
    const resetColumns = DEFAULT_COLUMNS.map((column, index) => ({
      ...column,
      position: index + 1
    }));
    setAllColumns(resetColumns);
    try {
      await saveAppUserColumnConfig(buildColumnPayload(resetColumns));
      toast.info('Columns reset to default');
    } catch (error) {
      toast.error('Failed to reset column configuration');
    }
  };

  const handleSaveColumnConfig = async (columns) => {
    await saveAppUserColumnConfig(buildColumnPayload(columns));
  };

  const handleColumnChange = async (updatedColumns) => {
    setAllColumns(updatedColumns);
    try {
      await handleSaveColumnConfig(updatedColumns);
    } catch (error) {
      toast.error('Failed to save column configuration');
    }
  };

  const handleDragStart = (event, id) => {
    isDraggingRef.current = true;
    setDraggedCol(id);
    event.dataTransfer.effectAllowed = 'move';
    event.currentTarget.style.opacity = '0.5';
  };

  const handleDragEnd = (event) => {
    event.currentTarget.style.opacity = '1';
    setTimeout(() => {
      isDraggingRef.current = false;
    }, 200);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (event, targetId) => {
    event.preventDefault();
    setTimeout(() => {
      isDraggingRef.current = false;
    }, 200);
    if (!draggedCol || draggedCol === targetId) return;

    const nextColumns = [...allColumns];
    const draggedIndex = nextColumns.findIndex((column) => column.id === draggedCol);
    const targetIndex = nextColumns.findIndex((column) => column.id === targetId);
    const [draggedItem] = nextColumns.splice(draggedIndex, 1);
    nextColumns.splice(targetIndex, 0, draggedItem);

    const reorderedColumns = nextColumns.map((column, index) => ({
      ...column,
      position: index + 1
    }));

    setAllColumns(reorderedColumns);
    setDraggedCol(null);

    try {
      await handleSaveColumnConfig(reorderedColumns);
    } catch (error) {
      toast.error('Failed to save column order');
    }
  };

  const handleAdvancedPanelReset = () => {
    setAdvanceFilters([]);
    setFilterForm((previous) => ({
      ...previous,
      profileDateEnabled: false,
      fromProfileCreationDate: '',
      toProfileCreationDate: '',
      subscriptionDateEnabled: false,
      fromSubscriptionEndDate: '',
      toSubscriptionEndDate: '',
      situationProfessionnelle: '',
      horizonDePlacement: '',
      objectifDePlacement: [],
      aversionAuRisque: ''
    }));
    setObjectiveDropdownOpen(false);
  };

  const toggleObjective = (objective) => {
    setFilterForm((previous) => {
      const alreadySelected = previous.objectifDePlacement.includes(objective);
      return {
        ...previous,
        objectifDePlacement: alreadySelected
          ? previous.objectifDePlacement.filter((item) => item !== objective)
          : [...previous.objectifDePlacement, objective]
      };
    });
  };

  const handleViewClick = (advancedFiltersOverride) => {
    if (pendingFilterApply.current) return;
    pendingFilterApply.current = true;

    const filtersToApply = Array.isArray(advancedFiltersOverride) ? advancedFiltersOverride : advanceFilters;

    const validationError = validateFilters(filtersToApply);
    if (validationError) {
      toast.error(validationError);
      pendingFilterApply.current = false;
      return;
    }

    appliedFiltersRef.current = {
      isFilterApplied: true,
      page: 1,
      limit: pageSize,
      sortColumn,
      sortOrder,
      advanceFilters: filtersToApply,
      ...filterForm
    };

    setIsFilterApplied(true);
    setCurrentPage(1);
    fetchUsers(1, pageSize, true);
  };

  const validateFilters = useCallback((advancedFiltersOverride = advanceFilters) => {
    if (filterForm.profileDateEnabled) {
      if (!filterForm.fromProfileCreationDate || !filterForm.toProfileCreationDate) {
        return 'Select both profile creation dates';
      }
      if (new Date(filterForm.fromProfileCreationDate) > new Date(filterForm.toProfileCreationDate)) {
        return 'Profile creation start date cannot be after end date';
      }
    }

    if (filterForm.subscriptionDateEnabled) {
      if (!filterForm.fromSubscriptionEndDate || !filterForm.toSubscriptionEndDate) {
        return 'Select both subscription end dates';
      }
      if (new Date(filterForm.fromSubscriptionEndDate) > new Date(filterForm.toSubscriptionEndDate)) {
        return 'Subscription start date cannot be after end date';
      }
    }

    const filtersToValidate = Array.isArray(advancedFiltersOverride) ? advancedFiltersOverride : advanceFilters;

    const invalidAdvanceRule = (filtersToValidate || []).find((filter) => (
      filter.field && !hasAdvancedFilterValue(filter.value)
    ));

    if (invalidAdvanceRule) {
      return 'Please enter a value for all selected advanced filter fields';
    }

    return '';
  }, [advanceFilters, filterForm]);

  const syncAppliedFiltersFromCurrentState = useCallback((advancedFiltersOverride = advanceFilters, page = currentPage, limit = pageSize) => {
    appliedFiltersRef.current = {
      isFilterApplied: true,
      page,
      limit,
      sortColumn,
      sortOrder,
      advanceFilters: advancedFiltersOverride,
      ...filterForm
    };
  }, [advanceFilters, currentPage, filterForm, pageSize, sortColumn, sortOrder]);

  const handleClearFilters = async () => {
    if (pendingFilterApply.current) return;
    pendingFilterApply.current = true;

    setFilterForm({
      profileDateEnabled: false,
      fromProfileCreationDate: '',
      toProfileCreationDate: '',
      subscriptionDateEnabled: false,
      fromSubscriptionEndDate: '',
      toSubscriptionEndDate: '',
      situationProfessionnelle: '',
      horizonDePlacement: '',
      objectifDePlacement: [],
      aversionAuRisque: ''
    });
    setAdvanceFilters([]);
    setIsFilterApplied(false);
    setCurrentPage(1);
    setPageSize(10);
    appliedFiltersRef.current = {
      isFilterApplied: false,
      page: 1,
      limit: 10,
      sortColumn,
      sortOrder,
      advanceFilters: [],
      profileDateEnabled: false,
      fromProfileCreationDate: '',
      toProfileCreationDate: '',
      subscriptionDateEnabled: false,
      fromSubscriptionEndDate: '',
      toSubscriptionEndDate: '',
      situationProfessionnelle: '',
      horizonDePlacement: '',
      objectifDePlacement: [],
      aversionAuRisque: ''
    };
    await fetchUsers(1, 10, false);
    pendingFilterApply.current = false;
  };

  const BASE_URL = 'https://api.amwaly.ma/';

  const downloadReport = async (type) => {
    // Validate filters before download
    const validationError = validateFilters();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    // Set loading state
    const isExcel = type === 'excel';
    isExcel ? setExcelDownloadLoading(true) : setDownloadLoading(true);

    try {
      // Sync current filter state
      syncAppliedFiltersFromCurrentState();
      setIsFilterApplied(true);

      // Build the payload
      const payload = buildFilterPayload(type, currentPage, pageSize);

      // Make the API request
      const response = await api.post('/admin/user/getappuserwithfilter', payload, {
        responseType: 'json'
      });

      // Check if we got a valid response
      if (!response || !response.data) {
        throw new Error('No response received from server');
      }

      const data = response.data;

      // Check for Base64File (same as UserQueriesReport)
      if (data.Base64File && typeof data.Base64File === 'string') {
        const contentType = isExcel
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/pdf';

        // Build blob from base64 using the same function as UserQueriesReport
        const blob = buildDownloadBase64(data.Base64File, contentType);

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        // Use filename from response or generate one
        const filename = data.FileName || `user_report_${Date.now()}.${isExcel ? 'xlsx' : 'pdf'}`;
        link.download = filename;

        document.body.appendChild(link);
        link.click();

        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.success(`${type.toUpperCase()} downloaded successfully!`);
      }
      // Handle DownloadLink (if API returns URL)
      else if (data.DownloadLink) {
        // Construct full URL with base URL
        const fullDownloadUrl = data.DownloadLink.startsWith('http')
          ? data.DownloadLink
          : `${BASE_URL}${data.DownloadLink.replace(/^\//, '')}`;

        // Extract filename from DownloadLink
        const urlParts = data.DownloadLink.split('/');
        const fileName = urlParts[urlParts.length - 1] || `user_report_${Date.now()}.${isExcel ? 'xlsx' : 'pdf'}`;

        // Fetch the file from the download link
        try {
          const fileResponse = await fetch(fullDownloadUrl, {
            method: 'GET',
            headers: {
              'Accept': isExcel
                ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                : 'application/pdf'
            }
          });

          if (!fileResponse.ok) {
            throw new Error(`Failed to download file: ${fileResponse.statusText}`);
          }

          // Get the file as blob
          const blob = await fileResponse.blob();

          // Validate blob size
          if (blob.size === 0) {
            throw new Error('Downloaded file is empty');
          }

          // Create download link
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = fileName;
          link.target = '_blank';

          document.body.appendChild(link);
          link.click();

          setTimeout(() => {
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          }, 100);

          toast.success(`${type.toUpperCase()} downloaded successfully!`);
        } catch (fetchError) {
          console.error('Error fetching file from download link:', fetchError);
          // Fallback: Open the download link in a new tab
          window.open(fullDownloadUrl, '_blank');
          toast.success(`${type.toUpperCase()} download started!`);
        }
      }
      // Handle data property (legacy)
      else if (data.data && typeof data.data === 'string') {
        const contentType = isExcel
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/pdf';

        const blob = buildDownloadBase64(data.data, contentType);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const filename = data.FileName || `user_report_${Date.now()}.${isExcel ? 'xlsx' : 'pdf'}`;
        link.download = filename;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.success(`${type.toUpperCase()} downloaded successfully!`);
      }
      else {
        toast.error(`No ${type.toUpperCase()} data received from server`);
      }

    } catch (error) {
      console.error('Download error:', error);

      // User-friendly error message
      let errorMessage = `Failed to download ${type.toUpperCase()}`;
      if (error.response) {
        errorMessage = error.response.data?.message || errorMessage;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      // Reset loading states
      isExcel ? setExcelDownloadLoading(false) : setDownloadLoading(false);
    }
  };

  const getCellValue = (user, columnId) => {
    switch (columnId) {
      case 'FullName':
        return user.fullName || 'N/A';
      case 'EmailID':
        return user.email || 'N/A';
      case 'ContactNumber':
        return user.contactNumber || 'N/A';
      case 'SubscriptionEndDate':
        return formatDate(user.subscriptionEndingDate);
      case 'DOB':
        return formatDate(user.dob);
      case 'FirstName':
        return user.firstName || 'N/A';
      case 'LastName':
        return user.lastName || 'N/A';
      default:
        return 'N/A';
    }
  };

  const appliedFilterItems = [];
  if (filterForm.profileDateEnabled && (filterForm.fromProfileCreationDate || filterForm.toProfileCreationDate)) {
    appliedFilterItems.push(`Profile Creation: ${filterForm.fromProfileCreationDate || '-'} to ${filterForm.toProfileCreationDate || '-'}`);
  }
  if (filterForm.subscriptionDateEnabled && (filterForm.fromSubscriptionEndDate || filterForm.toSubscriptionEndDate)) {
    appliedFilterItems.push(`Subscription: ${filterForm.fromSubscriptionEndDate || '-'} to ${filterForm.toSubscriptionEndDate || '-'}`);
  }
  if (filterForm.situationProfessionnelle) appliedFilterItems.push(`Situation: ${filterForm.situationProfessionnelle}`);
  if (filterForm.horizonDePlacement) appliedFilterItems.push(`Horizon: ${filterForm.horizonDePlacement}`);
  if (filterForm.objectifDePlacement.length) appliedFilterItems.push(`Objectives: ${filterForm.objectifDePlacement.join(', ')}`);
  if (filterForm.aversionAuRisque) appliedFilterItems.push(`Risk: ${filterForm.aversionAuRisque}`);
  advanceFilters.forEach((filter) => {
    const fieldLabel = filterFields.find((item) => item.value === filter.field)?.label || filter.field;
    const filterValue = Array.isArray(filter.value) ? filter.value.filter(Boolean).join(' - ') : filter.value;
    if (fieldLabel && filterValue) {
      appliedFilterItems.push(`${fieldLabel}: ${filterValue}`);
    }
  });

  return (
    <>
      {showDrawer && selectedUser && (
        <FilterUserDrawer
          selectedUser={selectedUser}
          onClose={handleCloseDrawer}
          onUpdateUser={handleUpdateUser}
          onUpdateSuccess={handleUpdateSuccess}
        />
      )}

      <div className="card h-100 p-0 radius-12 overflow-hidden report-compact-card d-flex justify-content-center">
        <div className="card-header border-bottom bg-base py-12 px-16">
          <div className="d-flex flex-column gap-12">
            <div className="d-flex flex-wrap justify-content-between gap-12 align-items-center">
              <div className="d-flex flex-wrap gap-2">
                <div className="d-flex align-items-center gap-2">
                  <span className="text-sm text-secondary-light fw-medium">Show:</span>
                  <div className="dropdown">
                    <button
                      className="btn btn-outline-secondary btn-sm h-40-px px-16 radius-6 d-flex align-items-center gap-2"
                      type="button"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                      disabled={loading}
                      style={{
                        minWidth: '70px',
                        justifyContent: 'space-between',
                        backgroundColor: '#fff',
                        borderColor: '#d1d5db',
                        fontWeight: '500',
                        fontSize: '13px'
                      }}
                    >
                      <span>{pageSize === 'All' ? 'All' : pageSize}</span>
                      <Icon icon="ion:chevron-down-outline" className="text-secondary-light" style={{ fontSize: '14px' }} />
                    </button>
                    <ul className="dropdown-menu shadow-sm border-0 radius-8 py-2" style={{ minWidth: '70px' }}>
                      {PAGE_SIZE_OPTIONS.map((option) => (
                        <li key={option}>
                          <button
                            className={`dropdown-item py-2 px-16 text-sm ${pageSize === option ? 'active bg-primary-50 text-primary-600' : ''}`}
                            onClick={() => handlePageSizeChange(option)}
                            disabled={loading}
                            style={{
                              fontWeight: pageSize === option ? '600' : '400',
                              fontSize: '13px',
                              borderRadius: '6px'
                            }}
                          >
                            {option}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <button
                  className="btn btn-primary btn-sm h-40-px d-inline-flex align-items-center gap-2"
                  onClick={handleViewClick}
                  disabled={loading}
                >
                  <Icon icon="ion:eye-outline" className="icon" />
                  View
                </button>
                <AdvancedFilter
                  fields={filterFields}
                  filters={advanceFilters}
                  onApply={(filters) => {
                    setAdvanceFilters(filters);
                    handleViewClick(filters);
                  }}
                  onClear={handleAdvancedPanelReset}
                  triggerLabel="Advance Filters"
                  onValidate={() => validateFilters()}
                  hasFilterValues={hasChildFilterValues}
                >
                  <div className="filter-block h-100 compact">
                    <div className="d-flex align-items-center justify-content-between mb-8">
                      <label className="form-label mb-0 fw-semibold text-sm">Profile Creation Date</label>
                      <div className="form-check form-switch m-0">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={filterForm.profileDateEnabled}
                          onChange={(event) => setFilterForm((previous) => ({ ...previous, profileDateEnabled: event.target.checked }))}
                        />
                      </div>
                    </div>
                    <div className="row g-2">
                      <div className="col-6">
                        <input
                          type="date"
                          className="form-control radius-8 h-40-px"
                          value={filterForm.fromProfileCreationDate}
                          onChange={(event) => setFilterForm((previous) => ({ ...previous, fromProfileCreationDate: event.target.value }))}
                          disabled={!filterForm.profileDateEnabled}
                        />
                      </div>
                      <div className="col-6">
                        <input
                          type="date"
                          className="form-control radius-8 h-40-px"
                          value={filterForm.toProfileCreationDate}
                          onChange={(event) => setFilterForm((previous) => ({ ...previous, toProfileCreationDate: event.target.value }))}
                          disabled={!filterForm.profileDateEnabled}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="filter-block h-100 compact">
                    <div className="d-flex align-items-center justify-content-between mb-8">
                      <label className="form-label mb-0 fw-semibold text-sm">Subscription End Date</label>
                      <div className="form-check form-switch m-0">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={filterForm.subscriptionDateEnabled}
                          onChange={(event) => setFilterForm((previous) => ({ ...previous, subscriptionDateEnabled: event.target.checked }))}
                        />
                      </div>
                    </div>
                    <div className="row g-2">
                      <div className="col-6">
                        <input
                          type="date"
                          className="form-control radius-8 h-40-px"
                          value={filterForm.fromSubscriptionEndDate}
                          onChange={(event) => setFilterForm((previous) => ({ ...previous, fromSubscriptionEndDate: event.target.value }))}
                          disabled={!filterForm.subscriptionDateEnabled}
                        />
                      </div>
                      <div className="col-6">
                        <input
                          type="date"
                          className="form-control radius-8 h-40-px"
                          value={filterForm.toSubscriptionEndDate}
                          onChange={(event) => setFilterForm((previous) => ({ ...previous, toSubscriptionEndDate: event.target.value }))}
                          disabled={!filterForm.subscriptionDateEnabled}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="filter-block h-100 compact">
                    <label className="form-label mb-8 fw-semibold text-sm">Situation</label>
                    <select
                      className="form-select radius-8 h-40-px"
                      value={filterForm.situationProfessionnelle}
                      onChange={(event) => setFilterForm((previous) => ({ ...previous, situationProfessionnelle: event.target.value }))}
                    >
                      <option value="">All situations</option>
                      {professionalSituations.map((situation) => (
                        <option key={situation.ID || situation.Value} value={situation.Value}>
                          {situation.Value}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="filter-block h-100 compact">
                    <label className="form-label mb-8 fw-semibold text-sm">Horizon</label>
                    <select
                      className="form-select radius-8 h-40-px"
                      value={filterForm.horizonDePlacement}
                      onChange={(event) => setFilterForm((previous) => ({ ...previous, horizonDePlacement: event.target.value }))}
                    >
                      <option value="">All horizons</option>
                      {HORIZON_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="filter-block h-100 compact">
                    <label className="form-label mb-8 fw-semibold text-sm">Risk</label>
                    <select
                      className="form-select radius-8 h-40-px"
                      value={filterForm.aversionAuRisque}
                      onChange={(event) => setFilterForm((previous) => ({ ...previous, aversionAuRisque: event.target.value }))}
                    >
                      <option value="">All risk levels</option>
                      {RISK_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="filter-block h-100 compact">
                    <div className="d-flex align-items-center justify-content-between mb-8">
                      <label className="form-label mb-0 fw-semibold text-sm">Objectives</label>
                      <span className="text-xs text-secondary-light">{filterForm.objectifDePlacement.length} selected</span>
                    </div>
                    <div className="position-relative" ref={objectiveDropdownRef}>
                      <button
                        type="button"
                        className="form-control h-40-px radius-8 text-start d-flex align-items-center justify-content-between objective-dropdown-trigger"
                        onClick={() => setObjectiveDropdownOpen((previous) => !previous)}
                      >
                        <span className="text-truncate">
                          {filterForm.objectifDePlacement.length
                            ? filterForm.objectifDePlacement.join(', ')
                            : 'Select objectives'}
                        </span>
                        <Icon icon={objectiveDropdownOpen ? 'ion:chevron-up-outline' : 'ion:chevron-down-outline'} className="icon" />
                      </button>

                      {objectiveDropdownOpen && (
                        <div className="objective-dropdown-menu shadow-sm">
                          {OBJECTIVE_OPTIONS.map((option) => {
                            const active = filterForm.objectifDePlacement.includes(option);
                            return (
                              <button
                                key={option}
                                type="button"
                                className={`objective-option ${active ? 'active' : ''}`}
                                onClick={() => toggleObjective(option)}
                              >
                                <div className="d-flex align-items-center gap-2">
                                  <input type="checkbox" checked={active} readOnly />
                                  <span>{option}</span>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </AdvancedFilter>
                <ColumnChooser
                  columns={allColumns}
                  onChange={handleColumnChange}
                  onReset={handleResetColumns}
                />
                <button
                  className="btn btn-success btn-sm h-40-px d-inline-flex align-items-center gap-2"
                  onClick={() => downloadReport('excel')}
                  disabled={loading || excelDownloadLoading}
                >
                  {excelDownloadLoading ? <div className="spinner-border spinner-border-sm" /> : <Icon icon="ri:file-excel-2-fill" className="icon" />}
                  Excel
                </button>
                <button
                  className="btn btn-danger btn-sm h-40-px d-inline-flex align-items-center gap-2"
                  onClick={() => downloadReport('pdf')}
                  disabled={loading || downloadLoading}
                >
                  {downloadLoading ? <div className="spinner-border spinner-border-sm" /> : <Icon icon="ri:file-pdf-2-fill" className="icon" />}
                  PDF
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="card-body px-10 py-6">
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading App Users...</p>
            </div>
          ) : (
            <>
              {appliedFilterItems.length > 0 && (
                <div className="compact-filter-summary mb-6">
                  {appliedFilterItems.map((item) => (
                    <span key={item} className="compact-filter-chip">{item}</span>
                  ))}
                </div>
              )}
              <div className="table-responsive scroll-sm">
                <table className="table bordered-table sm-table mb-0">
                  <thead>
                    <tr>
                      <th scope="col" className="text-center align-middle" style={{ width: `${APP_ACTION_COLUMN_WIDTH}px`, minWidth: `${APP_ACTION_COLUMN_WIDTH}px` }}>Action</th>
                      {visibleColumns.map((column) => (
                        <th
                          key={column.id}
                          draggable
                          onDragStart={(event) => handleDragStart(event, column.id)}
                          onDragEnd={handleDragEnd}
                          onDragOver={handleDragOver}
                          onDrop={(event) => handleDrop(event, column.id)}
                          onClick={() => {
                            if (!isDraggingRef.current && column.sortable) {
                              handleSort(column);
                            }
                          }}
                          className="position-relative align-middle"
                          style={{ userSelect: 'none', cursor: 'grab' }}
                        >
                          <div className="d-flex align-items-center justify-content-between gap-2">
                            <div className="d-flex align-items-center gap-2">
                              <Icon icon="ion:menu-outline" className="text-muted" title="Drag to reorder column" />
                              <span className="fw-medium">{column.label}</span>
                            </div>
                            {column.sortable && (
                              <span
                                role="button"
                                tabIndex={0}
                                className="d-inline-flex align-items-center justify-content-center p-1 rounded"
                                style={{ cursor: 'pointer' }}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleSort(column);
                                }}
                                title={`Sort by ${column.label}`}
                              >
                                <Icon
                                  icon={
                                    sortColumn === column.id
                                      ? (sortOrder === 'desc' ? 'ion:arrow-down-outline' : 'ion:arrow-up-outline')
                                      : 'ion:swap-vertical-outline'
                                  }
                                  className={`text-sm ${sortColumn === column.id ? 'text-primary fw-bold' : 'text-muted'}`}
                                />
                              </span>
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedUsers.length > 0 ? (
                      paginatedUsers.map((user) => (
                        <tr key={user.id}>
                          <td className="text-center align-middle">
                            <button
                              type="button"
                              className="bg-transparent border-0 text-secondary-light hover:text-primary-600 fw-medium w-32-px h-32-px d-inline-flex justify-content-center align-items-center rounded-circle"
                              onClick={() => handleViewDetails(user)}
                              title="View Details"
                            >
                              <Icon icon="bi:three-dots-vertical" className="icon text-lg" />
                            </button>
                          </td>
                          {visibleColumns.map((column) => (
                            <td key={`${user.id}-${column.id}`} className="align-middle">
                              {getCellValue(user, column.id)}
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={visibleColumns.length + 1} className="text-center py-4 align-middle">
                          {isFilterApplied
                            ? 'No app users found matching the selected filters'
                            : users.length === 0
                              ? 'No app users available'
                              : 'No users match your search'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-16">
                <div className="d-flex align-items-center gap-3">
                  <span className="text-secondary-light text-sm">
                    Showing {startEntry} to {endEntry} of {isFilterApplied ? totalRecords : (searchTerm.trim() ? localFilteredCount : totalRecords)} entries
                  </span>
                </div>

                {pageSize !== 'All' && effectiveTotalPages > 1 && (
                  <ul className="pagination d-flex flex-wrap align-items-center gap-2 m-0 p-0">
                    <li className="page-item">
                      <button
                        className="page-link bg-neutral-100 text-secondary-light radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        style={{ opacity: currentPage === 1 ? 0.4 : 1 }}
                      >
                        <Icon icon="ep:d-arrow-left" className="text-lg" />
                      </button>
                    </li>

                    {Array.from({ length: Math.min(5, effectiveTotalPages) }, (_, index) => {
                      let pageNumber;
                      if (effectiveTotalPages <= 5) {
                        pageNumber = index + 1;
                      } else if (currentPage <= 3) {
                        pageNumber = index + 1;
                      } else if (currentPage >= effectiveTotalPages - 2) {
                        pageNumber = effectiveTotalPages - 4 + index;
                      } else {
                        pageNumber = currentPage - 2 + index;
                      }
                      return (
                        <li className="page-item" key={pageNumber}>
                          <button
                            className={`page-link radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px fw-medium text-sm ${currentPage === pageNumber ? 'bg-primary-600 text-white shadow-sm' : 'bg-neutral-100 text-secondary-light'}`}
                            onClick={() => handlePageChange(pageNumber)}
                          >
                            {pageNumber}
                          </button>
                        </li>
                      );
                    })}

                    <li className="page-item">
                      <button
                        className="page-link bg-neutral-100 text-secondary-light radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === effectiveTotalPages}
                        style={{ opacity: currentPage === effectiveTotalPages ? 0.4 : 1 }}
                      >
                        <Icon icon="ep:d-arrow-right" className="text-lg" />
                      </button>
                    </li>
                  </ul>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <style jsx>{`
        .app-user-filter-panel {
          background: linear-gradient(135deg, rgba(14, 165, 233, 0.05), rgba(16, 185, 129, 0.04));
          border: 1px solid rgba(148, 163, 184, 0.2);
          border-radius: 14px;
          padding: 12px;
        }

        .filter-block {
          background: rgba(255, 255, 255, 0.88);
          border: 1px solid rgba(203, 213, 225, 0.8);
          border-radius: 12px;
          padding: 10px 12px;
          box-shadow: 0 4px 14px rgba(15, 23, 42, 0.04);
        }

        .filter-block.compact {
          padding: 8px 10px;
        }

        .filter-block .form-control,
        .filter-block .form-select {
          min-height: 40px;
          font-size: 13px;
        }

        .compact-filter-summary {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }

        .compact-filter-chip {
          display: inline-flex;
          align-items: center;
          min-height: 24px;
          padding: 2px 8px;
          border-radius: 999px;
          background: rgba(15, 23, 42, 0.05);
          border: 1px solid rgba(148, 163, 184, 0.3);
          font-size: 11px;
          line-height: 1.2;
          color: #334155;
        }

        .objective-dropdown-trigger {
          font-size: 13px;
          background: #fff;
          cursor: pointer;
        }

        .objective-dropdown-menu {
          position: absolute;
          top: calc(100% + 6px);
          left: 0;
          right: 0;
          z-index: 20;
          background: #fff;
          border: 1px solid rgba(203, 213, 225, 1);
          border-radius: 12px;
          padding: 6px;
          max-height: 180px;
          overflow-y: auto;
        }

        .objective-option {
          width: 100%;
          border: 0;
          background: transparent;
          border-radius: 8px;
          padding: 8px 10px;
          text-align: left;
          font-size: 13px;
          color: #334155;
        }

        .objective-option:hover,
        .objective-option.active {
          background: rgba(14, 165, 233, 0.08);
        }

        .report-compact-card :global(th),
        .report-compact-card :global(td) {
          vertical-align: middle;
        }
      `}</style>
    </>
  );
};

export default AppUserListReport;